import React, { useEffect, useMemo, useState } from 'react';
import { api, apiRequest } from '../../utils/api';
import { useTranslation } from 'react-i18next';
import { usePortalAddress } from '../../utils/portalAddress';
import { RegionFields } from './CompanySettings';   // the same Region fields Admin → Companies uses (package portalintl)

// Admin backoffice for the client invoicing portal (its address: Admin → Companies → Workspace → Client portal address).
// Create / reset / disable / delete portal logins; documents & merchant data always survive.

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 10px', fontSize: 13, borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-strong)', background: 'var(--c-gray-50)', color: 'var(--text-primary)', outline: 'none',
};
const btn = (bg: string, color = '#fff'): React.CSSProperties => ({
  padding: '8px 14px', borderRadius: 'var(--radius-md)', border: 'none', background: bg, color,
  fontSize: 13, fontWeight: 600, cursor: 'pointer',
});
// 11 → 12px (Calem, report 66c9d174, 2nd opening: "Do the same for all other menus in the SLF system"). Then Active
// Jobs' headings, as every staff list's (report 4bd4ae4e, 2nd opening: "Make all menus in the SLF system look like this:
// the same height, text color, no bold, horizontal line colors etc."): black, --text-primary, at the regular weight, where
// they were light grey at semibold (600), 10px above and below where it was 8, on one 16px line, on white, over the
// darker --border-strong line — a 37px band, as every list's is.
// 10 → 5px under the heading text (report c151d269: "Bring the heading text 5 pixels closer to the grey line
// below. Do this for every page." — see Active Jobs' th: same top padding, sides, size, colour and line).
const th: React.CSSProperties = { textAlign: 'left', fontSize: 12, fontWeight: 400, color: 'var(--text-primary)', padding: '10px 10px 5px', lineHeight: '16px', borderBottom: '1px solid var(--border-strong)', background: 'var(--bg-surface)' };
const td: React.CSSProperties = { fontSize: 13, color: 'var(--text-primary)', padding: '8px 10px', borderBottom: '1px solid var(--border)' };

function PasswordModal({ email, password, portalUrl, onClose }: { email: string; password: string; portalUrl: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const text = `Sign in: ${portalUrl || '—'}\nEmail: ${email}\nTemporary password: ${password}`;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ width: 460, background: 'var(--bg-surface)', borderRadius: 'var(--radius-xl)', border: '1px solid var(--border)', padding: 24, boxShadow: 'var(--shadow-md)' }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>One-time password</div>
        <div style={{ fontSize: 12.5, color: '#B45309', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '8px 12px', marginBottom: 12 }}>
          Shown only once — it is not stored anywhere. The client must change it on first sign-in.
        </div>
        <pre style={{ fontSize: 13, background: 'var(--c-gray-50)', border: '1px solid var(--border)', borderRadius: 8, padding: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0 }}>{text}</pre>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button onClick={() => { navigator.clipboard.writeText(text).then(() => setCopied(true)); }} style={btn('var(--c-gray-100)', 'var(--text-secondary)')}>
            {copied ? '✓ Copied' : 'Copy'}
          </button>
          <button onClick={onClose} style={btn('var(--accent)')}>Done</button>
        </div>
      </div>
    </div>
  );
}

// ── A merchant's own region (owner 2026-10-03) ──────────────────────────────────────────────────
// Country / currency / time zone of the merchant company. The COUNTRY decides the document world
// (server services/merchantMode.ts): Thailand = Thai tax documents; anywhere else = plain quotations,
// invoices and receipts. Saved through /portal-accounts so it works for whoever manages these logins.
const regionApi = {
  packs: () => apiRequest('GET', '/portal-accounts/country-packs'),
  get: (merchantId: string) => apiRequest('GET', `/portal-accounts/merchants/${merchantId}/region`),
  save: (merchantId: string, region: Record<string, string>) => apiRequest('PATCH', `/portal-accounts/merchants/${merchantId}/region`, { region }),
};
// ── The portal's button into this company's back office (owner 2026-10-16) ─────────────────────
// ⛔ OFF until somebody types an address here: the accounting workspace is the FIRM's screen and
// holds every other client's books, so a portal that offered a way in by default would be
// advertising it to people who must never reach it. Clearing the field takes the button away again.
// The address is the SITE only — the page and the company are added by the server, so the link
// always opens this company's own books and cannot be pointed at somebody else's by hand.
// ── …and its second destination (owner 2026-10-16) ─────────────────────────────────────────────
// 'books' points the same button at the client's OWN read-only books, inside their portal — no
// second site and no staff password, which is what a CLIENT clicking it needs. ⛔ It routes the
// button; it does not grant anything: those pages are already open to every portal client of this
// installation, so this screen must not be worded as permission.
const backOfficeApi = {
  get: (merchantId: string) => apiRequest('GET', `/portal-accounts/merchants/${merchantId}/back-office`),
  save: (merchantId: string, mode: string, base: string) =>
    apiRequest('PATCH', `/portal-accounts/merchants/${merchantId}/back-office`, { mode, base }),
};
type BoMode = 'off' | 'books' | 'workspace';
function BackOfficeModal({ merchantId, name, onClose, onSaved }: { merchantId: string; name: string; onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<BoMode>('off');
  const [base, setBase] = useState('');
  const [link, setLink] = useState('');
  const [err, setErr] = useState(''); const [busy, setBusy] = useState(false);
  useEffect(() => {
    backOfficeApi.get(merchantId)
      .then((d: any) => {
        setBase(String(d.base || '')); setLink(String(d.link?.url || ''));
        setMode(d.books ? 'books' : (d.base ? 'workspace' : 'off'));
      })
      .catch((e: any) => setErr(e.message));
  }, [merchantId]);
  const save = async () => {
    setBusy(true); setErr('');
    try {
      const d: any = await backOfficeApi.save(merchantId, mode, base.trim());
      setLink(String(d.link?.url || '')); onSaved();
    }
    catch (e: any) { setErr(e.message); setBusy(false); }
  };
  const choice = (k: BoMode, label: string, hint: string) => (
    <label key={k} style={{ display: 'flex', gap: 9, alignItems: 'flex-start', padding: '9px 11px', borderRadius: 9, cursor: 'pointer',
                            border: `1px solid ${mode === k ? 'var(--accent)' : 'var(--border)'}`,
                            background: mode === k ? 'var(--c-gray-50)' : 'transparent', marginBottom: 7 }}>
      <input type="radio" name="bo-mode" checked={mode === k} onChange={() => setMode(k)} style={{ marginTop: 2 }} />
      <span>
        <span style={{ display: 'block', fontSize: 13.5, fontWeight: 700, color: 'var(--text-primary)' }}>{label}</span>
        <span style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5, marginTop: 2 }}>{hint}</span>
      </span>
    </label>
  );
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ width: 620, maxWidth: '96vw', background: 'var(--bg-surface)', borderRadius: 'var(--radius-xl)', border: '1px solid var(--border)', padding: 24, boxShadow: 'var(--shadow-md)' }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>{t('pi_adm_bo_title', { name })}</div>
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginBottom: 14, lineHeight: 1.5 }}>{t('pi_adm_bo_hint')}</div>
        {choice('off', t('pi_adm_bo_mode_off'), t('pi_adm_bo_mode_off_hint'))}
        {choice('books', t('pi_adm_bo_mode_books'), t('pi_adm_bo_mode_books_hint'))}
        {choice('workspace', t('pi_adm_bo_mode_ws'), t('pi_adm_bo_mode_ws_hint'))}
        {mode === 'workspace' && (
          <input value={base} onChange={e => setBase(e.target.value)} placeholder={t('pi_adm_bo_ph')} autoFocus
            style={{ width: '100%', padding: '9px 11px', fontSize: 13.5, border: '1px solid var(--border-strong)', borderRadius: 8, boxSizing: 'border-box', marginTop: 3 }} />
        )}
        {/* What the button will actually open — the firm reads this before anybody clicks it. */}
        {mode === 'books'
          ? <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 8 }}>{t('pi_adm_bo_opens_books')}</div>
          : link
            ? <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 8, wordBreak: 'break-all' }}>{t('pi_adm_bo_opens')} {link}</div>
            : <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 8 }}>{t('pi_adm_bo_off')}</div>}
        {err && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '8px 12px', borderRadius: 8, marginTop: 10 }}>{err}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button onClick={onClose} style={btn('var(--c-gray-100)', 'var(--text-secondary)')}>{t('pi_adm_cancel')}</button>
          <button onClick={save} disabled={busy} style={{ ...btn('var(--accent)'), opacity: busy ? .6 : 1 }}>{busy ? t('pi_adm_saving') : t('pi_adm_save')}</button>
        </div>
      </div>
    </div>
  );
}

function RegionModal({ merchantId, name, onClose, onSaved }: { merchantId: string; name: string; onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation();
  const [own, setOwn] = useState<Record<string, string> | null>(null);
  const [inherit, setInherit] = useState<Record<string, string>>({});
  const [err, setErr] = useState(''); const [busy, setBusy] = useState(false);
  useEffect(() => {
    regionApi.get(merchantId).then((d: any) => { setOwn(d.regionOwn || {}); setInherit(d.regionEffective || {}); }).catch((e: any) => setErr(e.message));
  }, [merchantId]);
  const save = async () => {
    setBusy(true); setErr('');
    // Every field is sent: an emptied one must clear (= inherit the workspace) rather than be skipped.
    const keys = ['country', 'timezone', 'currency', 'currencySymbol', 'calendar', 'dateFormat', 'dialCode', 'localeNumber', 'localeDate'];
    const region: Record<string, string> = {};
    for (const k of keys) region[k] = String((own || {})[k] || '');
    try { await regionApi.save(merchantId, region); onSaved(); }
    catch (e: any) { setErr(e.message); setBusy(false); }
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ width: 760, maxWidth: '96vw', maxHeight: '90vh', overflowY: 'auto', background: 'var(--bg-surface)', borderRadius: 'var(--radius-xl)', border: '1px solid var(--border)', padding: 24, boxShadow: 'var(--shadow-md)' }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>{t('pi_adm_region_title', { name })}</div>
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginBottom: 14, lineHeight: 1.5 }}>{t('pi_adm_region_hint')}</div>
        {own && <RegionFields value={own} onChange={setOwn} inherit={inherit} loadPacks={regionApi.packs} />}
        {err && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '8px 12px', borderRadius: 8, marginTop: 10 }}>{err}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button onClick={onClose} style={btn('var(--c-gray-100)', 'var(--text-secondary)')}>{t('pi_adm_cancel')}</button>
          <button onClick={save} disabled={busy || !own} style={{ ...btn('var(--accent)'), opacity: busy || !own ? .6 : 1 }}>{busy ? t('pi_adm_saving') : t('pi_adm_save')}</button>
        </div>
      </div>
    </div>
  );
}

function CreateModal({ onClose, onCreated }: { onClose: () => void; onCreated: (email: string, password: string, added?: boolean) => void }) {
  const [clients, setClients] = useState<any[]>([]);
  const [q, setQ] = useState('');
  const [clientId, setClientId] = useState('');
  const [email, setEmail] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.adminPortalClients().then((r: any) => setClients(r.rows || [])).catch((e: any) => setErr(e.message)); }, []);
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return clients.filter(c => !needle || `${c.companyName} ${c.clientName} ${c.email}`.toLowerCase().includes(needle)).slice(0, 60);
  }, [clients, q]);
  const pick = (c: any) => { setClientId(c.id); setEmail(c.email || ''); };
  const picked = clients.find(c => c.id === clientId);
  const create = async () => {
    setErr(''); setBusy(true);
    try {
      const r: any = await api.adminPortalCreate(clientId, email.trim());
      // No password comes back when the address already had a portal and this company simply joined
      // it — the owner keeps the password they already use. Showing a password modal there would be
      // a lie, so say what actually happened instead.
      onCreated(email.trim() || (picked?.email || ''), r.password, !!r.added);
    } catch (e: any) { setErr(e.message); setBusy(false); }
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ width: 560, maxHeight: '86vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-surface)', borderRadius: 'var(--radius-xl)', border: '1px solid var(--border)', padding: 24, boxShadow: 'var(--shadow-md)' }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>New portal account</div>
        {!clientId ? (
          <>
            <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Search client…" style={inputStyle} />
            <div style={{ flex: 1, overflowY: 'auto', marginTop: 10, border: '1px solid var(--border)', borderRadius: 8 }}>
              {filtered.map(c => (
                <div key={c.id} onClick={() => pick(c)}
                  style={{ padding: '9px 12px', borderBottom: '1px solid var(--border)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.companyName || c.clientName}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{c.email || 'no email on record'}</div>
                  </div>
                  {/* Not a block — a company can have several people signing in, each with their own
                      password. This just says so out loud before staff add another. */}
                  {!!c.hasAccount && <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                    {c.loginCount > 1 ? `${c.loginCount} logins` : '1 login'} — adding another is fine
                  </span>}
                </div>
              ))}
              {filtered.length === 0 && <div style={{ padding: 16, fontSize: 12.5, color: 'var(--text-tertiary)' }}>No matches.</div>}
            </div>
          </>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 13.5, fontWeight: 700 }}>{picked?.companyName || picked?.clientName}
              <button onClick={() => setClientId('')} style={{ marginLeft: 10, border: 'none', background: 'none', color: 'var(--accent)', cursor: 'pointer', fontSize: 12 }}>change</button>
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)' }}>Login email</label>
              <input value={email} onChange={e => setEmail(e.target.value)} style={inputStyle} placeholder="client@example.com" />
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
              The merchant profile (name, tax ID, address, VAT status) is pre-filled from the client record.
              A one-time password is generated and shown to you next.
            </div>
          </div>
        )}
        {err && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '8px 12px', borderRadius: 8, marginTop: 10 }}>{err}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button onClick={onClose} style={btn('var(--c-gray-100)', 'var(--text-secondary)')}>Cancel</button>
          {!!clientId && <button onClick={create} disabled={busy || !email.trim()} style={{ ...btn('var(--accent)'), opacity: busy || !email.trim() ? .6 : 1 }}>{busy ? 'Creating…' : 'Create account'}</button>}
        </div>
      </div>
    </div>
  );
}

// One merchant's country and document mode, small — for a login's own company, each extra company on
// it and each merchant no login reaches (portalintl).
function ModeLabel({ x }: { x: any }) {
  const { t } = useTranslation();
  if (!x?.merchantId) return <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{t('pi_adm_no_profile')}</span>;
  return <>
    <div>{x.merchantCountry || t('pi_adm_inherits', { country: x.effectiveCountry || '—' })}</div>
    <div style={{ fontSize: 10.5, color: x.thaiTaxMode ? 'var(--text-tertiary)' : '#1d5ba6' }}>{x.thaiTaxMode ? t('pi_adm_mode_thai') : t('pi_adm_mode_plain')}</div>
  </>;
}

export default function PortalClients() {
  const { t } = useTranslation();
  const portal = usePortalAddress();   // server: firmSettings.portalBaseUrl
  const [rows, setRows] = useState<any[]>([]);
  // Merchant companies no login reaches — still listed so their country can be set (portalintl).
  const [orphans, setOrphans] = useState<any[]>([]);
  const [err, setErr] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [pwModal, setPwModal] = useState<{ email: string; password: string } | null>(null);
  const [notice, setNotice] = useState('');
  const [regionOf, setRegionOf] = useState<{ merchantId: string; name: string } | null>(null);
  const [boOf, setBoOf] = useState<{ merchantId: string; name: string } | null>(null);
  const unlink = async (r: any, x: any) => {
    if (!window.confirm(`Remove ${x.name} from ${r.email}?\n\nThey lose access to that company immediately. Its documents are untouched.`)) return;
    setBusy(true);
    try { await api.adminPortalUnlinkCompany(r.userId, x.clientId); load(); }
    catch (e: any) { window.alert(e?.message || 'Could not remove it.'); }
    finally { setBusy(false); }
  };
  const [busy, setBusy] = useState(false);

  const load = () => api.adminPortalAccounts().then((r: any) => { setRows(r.rows || []); setOrphans(r.merchantsWithoutLogin || []); }).catch((e: any) => setErr(e.message));
  const regionBtn = (merchantId: string, name: string) => (
    <button onClick={() => setRegionOf({ merchantId, name })} disabled={busy} title={t('pi_adm_region_btn')} aria-label={t('pi_adm_region')}
      style={{ border: 'none', background: 'none', color: 'var(--text-secondary)', cursor: 'pointer', padding: 0 }}><i className="ti ti-world" /></button>
  );
  const backOfficeBtn = (merchantId: string, name: string) => (
    <button onClick={() => setBoOf({ merchantId, name })} disabled={busy} title={t('pi_adm_bo_btn')} aria-label={t('pi_adm_bo_btn')}
      style={{ border: 'none', background: 'none', color: 'var(--text-secondary)', cursor: 'pointer', padding: 0, marginLeft: 8 }}><i className="ti ti-external-link" /></button>
  );
  useEffect(() => { load(); }, []);

  const resetPw = async (r: any) => {
    if (!window.confirm(`Reset the portal password for ${r.email}? The old password stops working immediately.`)) return;
    setBusy(true);
    try { const res: any = await api.adminPortalResetPw(r.userId); setPwModal({ email: r.email, password: res.password }); }
    catch (e: any) { window.alert(e.message); } finally { setBusy(false); }
  };
  const toggleActive = async (r: any) => {
    setBusy(true);
    try { await api.adminPortalSetActive(r.userId, !r.active); load(); }
    catch (e: any) { window.alert(e.message); } finally { setBusy(false); }
  };
  const del = async (r: any) => {
    if (!window.confirm(`Delete the portal login ${r.email}?\n\nIssued documents and numbering are kept — only the login is removed.`)) return;
    setBusy(true);
    try { await api.adminPortalDelete(r.userId); load(); }
    catch (e: any) { window.alert(e.message); } finally { setBusy(false); }
  };

  return (
    <div style={{ maxWidth: 1080, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
          {portal.url
            ? <>Client logins for the invoicing portal at <a href={portal.url} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }}>{portal.url}</a>.</>
            : portal.loaded ? <span style={{ color: '#B45309' }}>{t('ctrl_portal_unset_admin')}</span> : null}{' '}
          Telegram-approved registrations create their account automatically.
        </div>
        <div style={{ flex: 1 }} />
        <button onClick={() => setShowCreate(true)} style={btn('var(--accent)')}>
          <i className="ti ti-plus" style={{ fontSize: 13, marginRight: 6 }} aria-hidden />New portal account
        </button>
      </div>
      {err && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '8px 12px', borderRadius: 8, marginBottom: 12 }}>{err}</div>}
      <table style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--bg-surface)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
        <thead><tr>
          <th style={th}>Client</th><th style={th}>Login email</th><th style={th}>{t('pi_adm_country')}</th><th style={th}>VAT</th>
          <th style={th}>Docs (INV/QO/RE)</th><th style={th}>Disclaimer</th><th style={th}>Status</th><th style={th}></th>
        </tr></thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.userId} style={{ opacity: r.active ? 1 : 0.55 }}>
              <td style={td}>
                <div style={{ fontWeight: 600 }}>{r.companyName || r.clientName || '—'}</div>
                {!!r.trialUntil && <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>trial until {r.trialUntil}</div>}
                {/* Companies this login reaches BEYOND its own. Listed here so "what can this person
                    see?" is answerable from the list. Removing one withdraws access only — the
                    documents belong to the company. */}
                {(r.extraCompanies || []).map((x: any) => (
                  <div key={x.clientId} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: 'var(--text-secondary)', marginTop: 2 }}>
                    <span>+ {x.name || x.clientId}</span>
                    {/* Each extra company is its own merchant: its own country / document mode (portalintl). */}
                    {x.merchantId
                      ? <span style={{ color: x.thaiTaxMode ? 'var(--text-tertiary)' : '#1d5ba6' }}>· {x.merchantCountry || t('pi_adm_inherits', { country: x.effectiveCountry || '—' })} · {x.thaiTaxMode ? t('pi_adm_mode_thai') : t('pi_adm_mode_plain')}</span>
                      : <span style={{ color: 'var(--text-tertiary)' }}>· {t('pi_adm_no_profile')}</span>}
                    {!!x.merchantId && regionBtn(x.merchantId, x.name || '')}
                    <button onClick={() => unlink(r, x)} disabled={busy} title="Remove this company from the login"
                      style={{ border: 'none', background: 'none', color: '#B45309', cursor: 'pointer', padding: 0, fontSize: 11.5 }}>remove</button>
                  </div>
                ))}
              </td>
              <td style={td}>{r.email}{!!r.mustChange && <div style={{ fontSize: 10.5, color: '#B45309' }}>temp password not yet changed</div>}</td>
              {/* Which country this merchant's documents follow — its own, else the workspace's (portalintl). */}
              <td style={td}><ModeLabel x={r} /></td>
              <td style={td}>{r.vatRegistered ? 'VAT' : '—'}</td>
              <td style={td}>{r.merchantId ? `${r.invoices} / ${r.quotations} / ${r.receipts}` : 'no profile'}</td>
              <td style={td}>{r.disclaimerAt ? '✓ accepted' : 'pending'}</td>
              <td style={td}>
                <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: r.active ? '#E4F5EC' : '#FCEBEB', color: r.active ? '#1D9E75' : '#E24B4A' }}>
                  {r.active ? 'Active' : 'Disabled'}
                </span>
              </td>
              <td style={{ ...td, whiteSpace: 'nowrap', textAlign: 'right' }}>
                {!!r.merchantId && <span style={{ marginRight: 8 }}>{regionBtn(r.merchantId, r.companyName || r.clientName || '')}{backOfficeBtn(r.merchantId, r.companyName || r.clientName || '')}</span>}
                <button onClick={() => resetPw(r)} disabled={busy} title="Reset password" style={{ border: 'none', background: 'none', color: 'var(--text-secondary)', cursor: 'pointer', marginRight: 8 }}><i className="ti ti-key" /></button>
                <button onClick={() => toggleActive(r)} disabled={busy} title={r.active ? 'Disable login' : 'Enable login'} style={{ border: 'none', background: 'none', color: r.active ? '#B45309' : '#1D9E75', cursor: 'pointer', marginRight: 8 }}><i className={`ti ${r.active ? 'ti-lock' : 'ti-lock-open'}`} /></button>
                <button onClick={() => del(r)} disabled={busy} title="Delete login" style={{ border: 'none', background: 'none', color: '#E24B4A', cursor: 'pointer' }}><i className="ti ti-trash" /></button>
              </td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td style={td} colSpan={8}>No portal accounts yet.</td></tr>}
        </tbody>
      </table>
      {orphans.length > 0 && <>
        <div style={{ fontSize: 13.5, fontWeight: 700, margin: '20px 0 4px' }}>{t('pi_adm_orphans_title')}</div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>{t('pi_adm_orphans_hint')}</div>
        <table style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--bg-surface)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
          <thead><tr><th style={th}>{t('pi_adm_company')}</th><th style={th}>{t('pi_adm_country')}</th><th style={th}></th></tr></thead>
          <tbody>
            {orphans.map((x: any) => (
              <tr key={x.merchantId}>
                <td style={td}><div style={{ fontWeight: 600 }}>{x.name || '—'}</div></td>
                <td style={td}><ModeLabel x={x} /></td>
                <td style={{ ...td, textAlign: 'right' }}>{regionBtn(x.merchantId, x.name || '')}{backOfficeBtn(x.merchantId, x.name || '')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </>}
      {showCreate && (
        <CreateModal
          onClose={() => setShowCreate(false)}
          onCreated={(email, password, added) => {
            setShowCreate(false);
            if (added) setNotice(`${email} already had a portal — this company was added to it. The owner signs in with their existing password.`);
            else setPwModal({ email, password });
            load();
          }}
        />
      )}
      {!!notice && (
        <div style={{ marginTop: 12, padding: '10px 12px', borderRadius: 8, background: '#E8F3F0',
                      border: '1px solid #C5E3DA', color: '#0E7C66', fontSize: 13 }}>
          {notice}
          <button onClick={() => setNotice('')} style={{ border: 'none', background: 'none', color: '#0E7C66', cursor: 'pointer', float: 'right' }}>✕</button>
        </div>
      )}
      {regionOf && <RegionModal merchantId={regionOf.merchantId} name={regionOf.name} onClose={() => setRegionOf(null)} onSaved={() => { setRegionOf(null); load(); }} />}
      {boOf && <BackOfficeModal merchantId={boOf.merchantId} name={boOf.name} onClose={() => setBoOf(null)} onSaved={() => { setBoOf(null); load(); }} />}
      {pwModal && <PasswordModal email={pwModal.email} password={pwModal.password} portalUrl={portal.url} onClose={() => setPwModal(null)} />}
    </div>
  );
}
